import { useMemo, useState } from 'react';
import { Accordion, Alert, Checkbox, Group, Paper, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { WarningIcon } from '@phosphor-icons/react';
import { type RegexFlag, REGEX_FLAGS, runRegex, toSegments } from '@/lib/regex';
import { useStrings } from '@/i18n/useLang';

const SAMPLE = 'Kontakt: alice@example.com, bob@test.org — Tel: +49 30 1234567';

export function RegexTester() {
  const t = useStrings();
  const [pattern, setPattern] = useState('[\\w.]+@[\\w.]+');
  const [flags, setFlags] = useState<Record<RegexFlag, boolean>>({
    g: true,
    i: false,
    m: false,
    s: false,
    u: false,
    y: false,
  });
  const [text, setText] = useState(SAMPLE);

  const flagString = REGEX_FLAGS.filter((f) => flags[f]).join('');
  const result = useMemo(() => runRegex(pattern, flagString, text), [pattern, flagString, text]);
  const segments = useMemo(() => toSegments(text, result.matches), [text, result.matches]);

  return (
    <Stack gap="lg">
      <TextInput
        label={t.ui.pattern}
        value={pattern}
        onChange={(e) => setPattern(e.currentTarget.value)}
        styles={{ input: { fontFamily: 'monospace' } }}
        error={result.error ?? undefined}
        leftSection={<Text c="dimmed">/</Text>}
      />

      <Group gap="md">
        <Text fw={500} size="sm">
          {t.ui.flags}
        </Text>
        {REGEX_FLAGS.map((f) => (
          <Checkbox
            key={f}
            label={`${f} — ${t.ui.flagNames[f]}`}
            checked={flags[f]}
            onChange={(e) => setFlags({ ...flags, [f]: e.currentTarget.checked })}
          />
        ))}
      </Group>

      <Textarea
        label={t.ui.testText}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        autosize
        minRows={3}
        maxRows={10}
      />

      {result.error ? (
        <Alert color="red" icon={<WarningIcon size={18} />} variant="light">
          {result.error}
        </Alert>
      ) : (
        <div>
          <Text fw={600} mb={4}>
            {t.ui.result} · {result.matches.length} {t.ui.matches}
          </Text>
          <Paper
            withBorder
            radius="md"
            p="md"
            style={{
              background: 'var(--bg-input)',
              borderColor: 'var(--border-light)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
            }}
          >
            {segments.length === 0 ? (
              <Text c="dimmed">{t.ui.empty}</Text>
            ) : (
              segments.map((s, i) =>
                s.match ? (
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable order, static render
                  <mark key={i} style={{ background: 'var(--primary)', color: 'var(--bg)', borderRadius: 3 }}>
                    {s.text}
                  </mark>
                ) : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable order, static render
                  <span key={i}>{s.text}</span>
                ),
              )
            )}
          </Paper>
        </div>
      )}

      {result.matches.some((m) => m.groups.length > 0) && (
        <div>
          <Text fw={600} mb={4}>
            {t.ui.groups}
          </Text>
          <Stack gap={4}>
            {result.matches.slice(0, 10).map((m, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: match order is stable
              <Text key={i} size="sm" ff="monospace" c="dimmed">
                #{i + 1}: {m.value}
                {m.groups.length > 0 && ` → [${m.groups.join(', ')}]`}
              </Text>
            ))}
          </Stack>
        </div>
      )}

      <Accordion variant="separated">
        <Accordion.Item value="cheatsheet">
          <Accordion.Control>{t.ui.cheatsheet}</Accordion.Control>
          <Accordion.Panel>
            <Stack gap={4}>
              {t.ui.cheats.map((c) => (
                <Text key={c.token} size="sm">
                  <Text span ff="monospace" c="cyan">
                    {c.token}
                  </Text>{' '}
                  — {c.desc}
                </Text>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
